import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  HttpLink,
  Observable,
  split,
  type NormalizedCacheObject,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import * as Sentry from '@sentry/react';
import { AUTH_TYPE, createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';
import type { AwsCredentialIdentityProvider } from '@aws-sdk/types';
import { resolveApiUrl } from '@referee/shared-client/api-origin';

/**
 * Wraps a subscription link with Sentry breadcrumb instrumentation.
 *
 * Emits breadcrumbs for:
 * - subscription start (every subscription request)
 * - first data event received (proves the subscription link delivered at least once; not emitted on every event to avoid flooding)
 * - error events
 * - completion events
 *
 * This is a no-op when Sentry is not initialised (safe in test/non-browser contexts).
 */
function createSubscriptionInstrumentationLink(innerLink: ApolloLink): ApolloLink {
  return new ApolloLink((operation) => {
    const definition = getMainDefinition(operation.query);
    const operationName =
      (definition.kind === 'OperationDefinition' && definition.name?.value) || 'unknown';

    Sentry.addBreadcrumb({
      category: 'appsync.subscription',
      message: `Subscription started: ${operationName}`,
      level: 'info',
    });

    const observable = innerLink.request(operation);
    if (!observable) {
      return null;
    }

    return new Observable((observer) => {
      let dataCount = 0;

      const sub = observable.subscribe({
        next: (data) => {
          dataCount++;
          if (dataCount === 1) {
            Sentry.addBreadcrumb({
              category: 'appsync.subscription',
              message: `Subscription first data: ${operationName}`,
              level: 'info',
            });
          }
          observer.next(data);
        },
        error: (err: unknown) => {
          Sentry.addBreadcrumb({
            category: 'appsync.subscription',
            message: `Subscription error: ${operationName}`,
            level: 'error',
            data: {
              error: err instanceof Error ? err.message : String(err),
            },
          });
          observer.error(err);
        },
        complete: () => {
          Sentry.addBreadcrumb({
            category: 'appsync.subscription',
            message: `Subscription complete: ${operationName}`,
            level: 'warning',
          });
          observer.complete();
        },
      });

      return () => sub.unsubscribe();
    });
  });
}

export interface ApolloClientConfig {
  graphqlEndpoint: string;
  apiKey?: string;
  getToken?: () => string | null;
  /** NEW — Cognito Identity Pool IAM credentials path (test + federation login page) */
  credentials?: AwsCredentialIdentityProvider;
  onAuthError?: () => void;
  /** AppSync region (required for subscriptions) */
  region?: string;
  /** AppSync API hostname for subscription auth headers (e.g. xxx.appsync-api.eu-central-1.amazonaws.com).
   *  When provided, subscriptions connect through graphqlEndpoint (same-origin) and the host field
   *  in auth headers is overridden to this value. */
  appSyncHost?: string;
  /** Native federation mode only (89 D-03): links that run before auth/http, e.g. the offline catalog cache. */
  prependLinks?: ApolloLink[];
}

export function createApolloClient(
  config: ApolloClientConfig,
): ApolloClient<NormalizedCacheObject> {
  const { apiKey, onAuthError, region = 'eu-central-1', appSyncHost, prependLinks } = config;
  // Identity on web; absolute origin on native (see @referee/shared-client/api-origin).
  const graphqlEndpoint = resolveApiUrl(config.graphqlEndpoint);

  const httpLink = new HttpLink({ uri: graphqlEndpoint, credentials: 'include' });

  const errorLink = onError(({ networkError }) => {
    if (
      onAuthError &&
      networkError &&
      'statusCode' in networkError &&
      networkError.statusCode === 401
    ) {
      onAuthError();
    }
  });

  // --- Subscription links ---
  // CRITICAL: createSubscriptionHandshakeLink locks auth.type at construction time.
  // A single link instance CANNOT switch between IAM and Cognito per-request.
  // For dual-auth (federation: Cognito when logged in, IAM on login page),
  // we create TWO subscription link instances and route via Apollo split().
  const subscriptionUrl = graphqlEndpoint;

  // SigV4 signing requires a full https:// URL (not a relative path like '/graphql')
  // to derive the host header for the signature. When using IAM credentials through
  // a CloudFront proxy, we must pass the real AppSync endpoint to the auth/subscription
  // links so the signer can parse host + path correctly.
  const iamUrl = appSyncHost ? `https://${appSyncHost}/graphql` : graphqlEndpoint;

  const cognitoAuth = {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS as const,
    jwtToken: () => config.getToken?.() ?? '',
  };

  const iamAuth = config.credentials
    ? { type: AUTH_TYPE.AWS_IAM as const, credentials: config.credentials }
    : null;

  const apiKeyAuth = apiKey ? { type: AUTH_TYPE.API_KEY as const, apiKey } : null;

  let subscriptionLink: ApolloLink;

  if (config.getToken && iamAuth) {
    // Dual-auth (federation): split based on whether a Cognito token currently exists.
    // Cognito when logged in, IAM when on the anonymous login page.
    const cognitoSubLink = createSubscriptionHandshakeLink(
      { url: subscriptionUrl, region, auth: cognitoAuth },
      httpLink,
    );
    const iamSubLink = createSubscriptionHandshakeLink(
      { url: iamUrl, region, auth: iamAuth },
      httpLink,
    );
    subscriptionLink = split(
      () => !!(config.getToken?.()),
      cognitoSubLink,
      iamSubLink,
    );
  } else if (iamAuth) {
    // IAM-only (test frontend): unauthenticated Cognito Identity Pool credentials
    subscriptionLink = createSubscriptionHandshakeLink(
      { url: iamUrl, region, auth: iamAuth },
      httpLink,
    );
  } else if (apiKeyAuth) {
    // Legacy API key path (deprecated — no frontend uses after phase 48)
    subscriptionLink = createSubscriptionHandshakeLink(
      { url: subscriptionUrl, region, auth: apiKeyAuth },
      httpLink,
    );
  } else {
    // Cognito-only (admin frontend and other standard authenticated paths)
    subscriptionLink = createSubscriptionHandshakeLink(
      { url: subscriptionUrl, region, auth: cognitoAuth },
      httpLink,
    );
  }

  // Wrap subscriptionLink with Sentry breadcrumb instrumentation so that
  // WebSocket subscription lifecycle events appear in Sentry error trails.
  // createSubscriptionInstrumentationLink is a no-op when Sentry is not initialised.
  subscriptionLink = createSubscriptionInstrumentationLink(subscriptionLink);

  // Split: subscriptions → AppSync WS link, queries/mutations → HTTP link
  const transportLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    subscriptionLink,
    httpLink,
  );

  // Override host in subscription start messages so AppSync accepts the auth.
  // The subscription library derives host from the URL (= CloudFront domain),
  // but AppSync requires its own API domain. graphql_headers in the library
  // spreads AFTER host (line 494), so this override takes effect.
  const subscriptionHostLink = appSyncHost
    ? setContext((operation, prevContext) => {
        const definition = getMainDefinition(operation.query);
        if (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        ) {
          return {
            ...prevContext,
            headers: {
              ...(prevContext.headers as Record<string, string> | undefined),
              host: appSyncHost,
            },
          };
        }
        return prevContext;
      })
    : null;

  // --- HTTP auth link chain ---
  // Priority: Cognito JWT token > IAM SigV4 > API key > no auth header
  const links: ApolloLink[] = [...(prependLinks ?? []), errorLink];

  if (config.credentials && config.getToken) {
    // Dual-auth path (federation): split HTTP auth between Cognito and IAM per-request
    const cognitoHttpLink = setContext((_, { headers }) => {
      const token = config.getToken?.();
      return {
        headers: { ...headers, ...(token ? { authorization: token } : {}) },
      };
    });
    const iamHttpLink = createAuthLink({
      url: iamUrl,
      region,
      auth: { type: AUTH_TYPE.AWS_IAM as const, credentials: config.credentials },
    });
    const dualHttpAuth = split(
      () => !!(config.getToken?.()),
      cognitoHttpLink,
      iamHttpLink,
    );
    links.push(dualHttpAuth);
  } else if (config.credentials) {
    // IAM-only path (test frontend): SigV4 signing for all HTTP requests
    const iamHttpLink = createAuthLink({
      url: iamUrl,
      region,
      auth: { type: AUTH_TYPE.AWS_IAM as const, credentials: config.credentials },
    });
    links.push(iamHttpLink);
  } else if (apiKey) {
    // Legacy API key path
    const apiKeyLink = setContext((_, { headers }) => ({
      headers: {
        ...headers,
        'x-api-key': apiKey,
      },
    }));
    links.push(apiKeyLink);
  } else if (config.getToken) {
    // Cognito-only: inject token as authorization header
    const cognitoLink = setContext((_, { headers }) => {
      const token = config.getToken?.();
      return {
        headers: { ...headers, ...(token ? { authorization: token } : {}) },
      };
    });
    links.push(cognitoLink);
  }

  if (subscriptionHostLink) {
    links.push(subscriptionHostLink);
  }
  links.push(transportLink);

  return new ApolloClient({
    link: ApolloLink.from(links),
    cache: new InMemoryCache({
      possibleTypes: {
        Catalog: ['SystemCatalog', 'CustomCatalog'],
      },
      typePolicies: {
        // Answer objects use single-letter option IDs ('a', 'b', 'c', 'd') that are
        // shared across all questions. Without keyFields: false, Apollo normalizes them
        // as Answer:a, Answer:b, etc. — causing cache collisions where the score for
        // option 'a' from one question overwrites option 'a' from another question.
        // Disable normalization so each Answer is stored inline within its parent Question.
        Answer: {
          keyFields: false,
        },
        // Question objects in questionResults use the catalog question ID as their `id`
        // field — the same question ID appears in every participant's questionResults.
        // Without keyFields: false, Apollo normalizes them as Question:<questionId>,
        // causing cache collisions where one participant's score/selectedAnswerIds for
        // question q-abc overwrites another participant's data for the same question.
        // This manifests as status-box corruption when page 2 is fetched via fetchMore:
        // page-2 questionResults overwrite Question:<qid> entries, and page-1 tests with
        // empty answersRef maps (PENDING tests) get re-seeded with page-2 answers.
        // Disable normalization so each Question is stored inline within its parent Test.
        Question: {
          keyFields: false,
        },
        Organization: {
          fields: {
            categories: {
              merge(_existing, incoming) {
                return incoming;
              },
            },
            ageGroups: {
              merge(_existing, incoming) {
                return incoming;
              },
            },
          },
        },
        Session: {
          fields: {
            tests: {
              keyArgs: ['sort'],
              merge(existing, incoming, { args }) {
                // No cursor = fresh fetch (initial load or sort change) — replace
                if (!args?.cursor) {
                  return incoming;
                }
                // With cursor = next page via fetchMore — append with dedup
                const existingTests = existing?.tests ?? [];
                const incomingTests = incoming?.tests ?? [];
                const seen = new Set(existingTests.map((t: { __ref?: string }) => t.__ref));
                const deduped = incomingTests.filter((t: { __ref?: string }) => !seen.has(t.__ref));
                return {
                  ...incoming,
                  tests: [...existingTests, ...deduped],
                };
              },
            },
          },
        },
        Query: {
          fields: {
            listAdminSessions: {
              keyArgs: ['params', ['federationId']],
              merge(existing, incoming, { args }) {
                // No cursor = fresh fetch — replace
                if (!args?.params?.cursor) {
                  return incoming;
                }
                // With cursor = next page via fetchMore — append sessions with dedup
                const existingSessions = existing?.sessions ?? [];
                const incomingSessions = incoming?.sessions ?? [];
                const seen = new Set(existingSessions.map((s: { __ref?: string }) => s.__ref));
                const deduped = incomingSessions.filter((s: { __ref?: string }) => !seen.has(s.__ref));
                return {
                  ...incoming,
                  sessions: [...existingSessions, ...deduped],
                };
              },
            },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
}
